import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";

export default function AdminDashboard() {
  const { profile, logout } = useAuth();

  const [data, setData] = useState({
    enrollments: [],
    tours: [],
    contacts: [],
    parents: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;

    async function loadDashboard() {
      setLoading(true);
      setError("");

      const loadRecent = async (name) => {
        const snapshot = await getDocs(
          query(
            collection(db, name),
            orderBy("createdAt", "desc"),
            limit(20)
          )
        );

        return snapshot.docs.map((record) => ({
          ...record.data(),
          id: record.id,
        }));
      };

      const loadParents = async () => {
        const snapshot = await getDocs(
          query(
            collection(db, "users"),
            where("role", "==", "parent")
          )
        );

        return snapshot.docs
          .map((record) => ({
            ...record.data(),
            id: record.id,
          }))
          .sort(
            (a, b) =>
              (b.createdAt?.toMillis?.() || 0) -
              (a.createdAt?.toMillis?.() || 0)
          );
      };

      const results = await Promise.allSettled([
        loadRecent("enrollments"),
        loadRecent("tourRequests"),
        loadRecent("contactMessages"),
        loadParents(),
      ]);

      if (!active) return;

      const keys = ["enrollments", "tours", "contacts", "parents"];
      const labels = [
        "enrollment requests",
        "tour requests",
        "contact messages",
        "parent accounts",
      ];

      const nextData = {
        enrollments: [],
        tours: [],
        contacts: [],
        parents: [],
      };

      const failures = [];

      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          nextData[keys[index]] = result.value;
        } else {
          nextData[keys[index]] = null;
          failures.push(labels[index]);
          console.error(`Unable to load ${labels[index]}:`, result.reason);
        }
      });

      setData(nextData);

      if (failures.length > 0) {
        setError(
          `Could not load ${failures.join(", ")}. Please try refreshing.`
        );
      }

      setLoading(false);
    }

    loadDashboard();

    return () => {
      active = false;
    };
  }, [refreshKey]);

  async function handleLogout() {
    try {
      await logout();
    } catch (logoutError) {
      console.error("Unable to sign out:", logoutError);
      setError("Unable to sign out. Please try again.");
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 rounded-3xl bg-slate-900 p-7 text-white md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm text-white/70">Administration</p>

          <h1 className="text-3xl font-bold text-white">
            Elsie’s Admin Dashboard
          </h1>

          <p className="mt-1 text-sm text-white/70">
            Signed in as {profile?.name || "Administrator"}
          </p>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            disabled={loading}
            onClick={() => setRefreshKey((value) => value + 1)}
            className="rounded-xl border border-white/40 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>

          <button
            type="button"
            onClick={handleLogout}
            className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-900"
          >
            Sign Out
          </button>
        </div>
      </div>

      {error && (
        <p
          role="alert"
          className="rounded-xl bg-red-50 p-4 text-red-700"
        >
          {error}
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          n={loading ? "…" : data.parents?.length ?? "—"}
          label="Registered parent profiles"
        />

        <Stat
          n={loading ? "…" : data.enrollments?.length ?? "—"}
          label="Recent enrollments (up to 20)"
        />

        <Stat
          n={loading ? "…" : data.tours?.length ?? "—"}
          label="Recent tour requests (up to 20)"
        />

        <Stat
          n={loading ? "…" : data.contacts?.length ?? "—"}
          label="Recent messages (up to 20)"
        />
      </div>

      <Records
        title="Parent Accounts"
        rows={data.parents}
        loading={loading}
        fields={[
          { key: "name", label: "Parent/Guardian" },
          { key: "email", label: "Email" },
          { key: "createdAt", label: "Account Created" },
        ]}
      />

      <Records
        title="Enrollment Requests"
        rows={data.enrollments}
        loading={loading}
        fields={[
          { key: "parentName", label: "Parent/Guardian" },
          { key: "childName", label: "Child" },
          { key: "program", label: "Program" },
          { key: "phone", label: "Phone" },
        ]}
      />

      <Records
        title="Tour Requests"
        rows={data.tours}
        loading={loading}
        fields={[
          { key: "name", label: "Name" },
          { key: "preferredDate", label: "Preferred Date" },
          { key: "email", label: "Email" },
          { key: "phone", label: "Phone" },
        ]}
      />

      <Records
        title="Contact Messages"
        rows={data.contacts}
        loading={loading}
        fields={[
          { key: "name", label: "Name" },
          { key: "email", label: "Email" },
          { key: "phone", label: "Phone" },
          { key: "message", label: "Message" },
        ]}
      />
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
      <div className="text-3xl font-bold text-sky-700">{n}</div>
      <div className="mt-1 text-sm text-slate-600">{label}</div>
    </div>
  );
}

function formatValue(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (typeof value.toDate === "function") {
    return value.toDate().toLocaleDateString();
  }

  return String(value);
}

function Records({ title, rows, fields, loading }) {
  return (
    <section className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
      <h2 className="text-xl font-bold text-slate-900">{title}</h2>

      {loading ? (
        <p role="status" className="mt-3 text-sm text-slate-500">
          Loading records…
        </p>
      ) : rows === null ? (
        <p className="mt-3 text-sm text-red-700">
          Records could not be loaded. Use Refresh to try again.
        </p>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">No records yet.</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <caption className="sr-only">{title}</caption>

            <thead>
              <tr>
                {fields.map((field) => (
                  <th
                    key={field.key}
                    scope="col"
                    className="border-b p-2 font-semibold"
                  >
                    {field.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((record) => (
                <tr key={record.id}>
                  {fields.map((field) => (
                    <td
                      key={field.key}
                      className="max-w-md whitespace-pre-wrap break-words border-b p-2 align-top"
                    >
                      {formatValue(record[field.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
